import { useResourceById } from "hooks/resources/useResourceById";
import { Link, useParams } from "react-router-dom";
import { Flex, Spinner, Text, Skeleton } from "@chakra-ui/react";
import { Center } from "@chakra-ui/react";
import PageHeader from "components/layout/PageHeader";
import { useCustomFormats } from "hooks/useCustomFormat";
import WidgetBox from "components/elements/WidgetBox/widget-box";
import CompanyBorrowers from "pages/Company/CompanyBorrowers";
import { PropertyViewModel } from "@common/properties/property.model";
import { Map } from "components/elements/Map/map";
import { WidgetBoxActions } from "components/elements/WidgetBox/widget-box-actions";
import { BindLoanButton } from "components/properties/components/bind-loan-button";
import PropertyLoansTable from "components/elements/PropertyLoanTable";
import { hasPermission } from "utils/permissionHelper";
import { useState } from "react";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { PropertyEditButton } from "./components/property-edit-button";
import { PropertyDetails } from "./components/property-details/property-details";

export function Property() {
  const { propertyId } = useParams();

  const {
    data: property,
    refetch: refetchProperty,
    isRefetching: isPropertyLoading,
  } = useResourceById<PropertyViewModel>("Properties", propertyId!);

  const { formatDate, formatCurrency } = useCustomFormats();

  function Details({
    title,
    description,
    link,
    isLoading = false,
  }: {
    title: string;
    description?: string | null;
    link?: string;
    isLoading?: boolean;
  }) {
    return (
      <Flex direction="column">
        <Text fontWeight={400} fontSize="14px" color="gray.200">
          {title}
        </Text>

        {isLoading ? (
          <Flex direction="column">
            <Skeleton height="20px" width="100%" />
          </Flex>
        ) : link ? (
          <Text as={Link} to={link} fontWeight={500} color="secondary" fontSize="16px">
            {description}
          </Text>
        ) : (
          <Text fontWeight={500} color="gray.900" fontSize="16px">
            {description ?? "Not Provided"}
          </Text>
        )}
      </Flex>
    );
  }

  function getBreadcrumbs() {
    if (!property) return [];

    const breadcrumbs: { title: string; to?: string }[] = [
      {
        title: "Borrowers",
        to: "/borrowers",
      },
      {
        title: property.company.name,
        to: `/companies/${property.company.id}`,
      },
    ];

    if (property.entity) {
      breadcrumbs.push({
        title: property.entity?.name ?? "",
        to: `/companies/${property.company.id}/entities/${property.entity?.id}`,
      });
    }

    breadcrumbs.push({
      title: property.name,
    });

    return breadcrumbs;
  }

  const { user } = useTenant();
  const [refreshTable, setRefreshTable] = useState(false);
  const isReadPropertyAllowed = hasPermission(user, "read_property");
  const isReadAllPropertyOnlyAllowed = hasPermission(user, "read_all_property_only");

  function handleRefreshData() {
    refetchProperty();
    setRefreshTable(!refreshTable);
  }

  return (
    <>
      {!property ? (
        <Center>
          <Spinner />
        </Center>
      ) : (
        <Flex direction="column" gap={4}>
          <PageHeader title={property.name} breadcrumbs={getBreadcrumbs()} />

          <WidgetBox title="Property">
            <WidgetBoxActions>
              <PropertyEditButton property={property} onUpdate={handleRefreshData} />
            </WidgetBoxActions>
            <Flex justifyContent="space-between">
              <Details
                title="Address"
                description={property.address}
                isLoading={isPropertyLoading}
              />
              <Details
                title="Total Loan Amount"
                description={formatCurrency(property.totalLoanAmount)}
                isLoading={isPropertyLoading}
              />
              <Details
                title="Purchase Date"
                description={formatDate(property.purchase_date ?? "")}
                isLoading={isPropertyLoading}
              />
              <Details
                title="Total Loans"
                description={property.totalLoans.toString()}
                isLoading={isPropertyLoading}
              />
            </Flex>
          </WidgetBox>

          <Flex direction="column" gap={4}>
            <Flex gap={4} maxH="300px">
              <CompanyBorrowers
                company={property.company.id}
                property={property.id}
                entity={property.entity?.id}
                title="Contacts"
              />
              <Map address={property.address} width="100%" />
            </Flex>

            {/* Property Details */}
            <PropertyDetails propertyId={property.id} />

            {/* Entity */}
            {property.entity && (
              <WidgetBox title="Entity">
                <Flex justifyContent="space-between">
                  <Details
                    title="Name"
                    description={property.entity.name}
                    link={`/companies/${property.company.id}/entities/${property.entity.id}`}
                  />
                  <Details title="Address" description={property.entity.address} />
                  <Details
                    title="Established Date"
                    description={formatDate(property.entity.established_date)}
                  />
                </Flex>
              </WidgetBox>
            )}

            {/* Loans */}
            <WidgetBox title="All Loans">
              <WidgetBoxActions>
                {!isReadAllPropertyOnlyAllowed && !isReadPropertyAllowed && property.id && (
                  <BindLoanButton
                    setRefreshTable={() => handleRefreshData()}
                    propertyId={property.id.toString()}
                  />
                )}
              </WidgetBoxActions>
              <PropertyLoansTable
                key={refreshTable ? "refresh" : "no-refresh"}
                small
                property_id={property.id.toString()}
                columns={[
                  "Date",
                  "Loan Number",
                  "Loan Officer",
                  "Borrower",
                  "Name",
                  "Lien Type",
                  "Type",
                  "Status",
                  "Amount",
                ]}
              />
            </WidgetBox>
          </Flex>
        </Flex>
      )}
    </>
  );
}
